import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash.throttle';
import { hot } from 'react-hot-loader';
import { TrackingContext } from '@vrbo/react-event-tracking';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { createInteractionEvent } from '../../utils/EventProviders/EgClickstreamUtil';
import AppActions from '../../actions/app';
import ClassificationActions from '../../actions/classification';
import { getUlxVisitorId } from '../../utils/CookieUtil';
import withPageFlow from '../../components/PageFlow/PageFlow';
import { CLASSIFICATION, DOM_UTILS_CTX } from '../../components/PageFlow/PageList';

export const DomUtilsCtx = createContext();

const isTopOfPage = () => {
    /* istanbul ignore else */
    if (typeof window === 'object') { // protect against SSR.
        return window.pageYOffset === 0;
    }
    return true;
};

const getPropsFromState = (state) => {
    const { app, router = {} } = state;
    return {
        query: app.query,
        location: router?.location?.pathname
    };
};


export const DomUtilsHandler = ({
    children,
    pageFlow
}) => {
    const { trigger } = useContext(TrackingContext);

    const dispatch = useDispatch();
    const setLodgingPreference = lodgingPreference => dispatch(
        ClassificationActions.setLodgingPreference(lodgingPreference)
    );
    const setQuery = query => dispatch(AppActions.setQuery(query));
    const {
        query, location
    } = useSelector(getPropsFromState, shallowEqual);

    // This function will be called to handle the lodging preference change
    // IMPROVEMENT: this function would be more appropriately placed in a navigation
    // context or a similar context that handles navigation events, but that refactor is
    // out of scope for this change. For now, it remains here to handle the lodging preference
    const handleLodgingPreferenceChange = async (
        lodgingPreference,
        actionLocation = 'classification_buttons' // default to classification buttons
    ) => {
        const gaData = {
            lodgingPreference,
            event: 'ClassificationData',
            id: 'ClassificationDataSubmit'
        };

        trigger('gtmEvent', gaData);
        const interactionEventName = `${lodgingPreference.toLowerCase()}_button.selected`;
        trigger(
            'interaction',
            createInteractionEvent(
                interactionEventName,
                'Classification',
                'Classification',
                actionLocation
            )
        );
        const duaid = getUlxVisitorId();
        setQuery({ ...query, epc_duaid: duaid });
        await setLodgingPreference(lodgingPreference);
        pageFlow.next(CLASSIFICATION);
    };

    const [top, setTop] = useState(isTopOfPage());
    const [isLypFormVisible, setIsLypFormVisible] = useState(true);

    const lypFormRef = useRef(null);

    const getLypFormVisbility = () => {
        /* istanbul ignore else */
        if (lypFormRef.current) { // protect against SSR.
            // eslint-disable-next-line max-len
            return (lypFormRef.current.getBoundingClientRect().top + lypFormRef.current.offsetTop) > 0;
        }
        return true;
    };
    const fetchLypForm = () => {
        if (!lypFormRef.current && typeof document === 'object') {
            // if the ref is not set yet, return early
            lypFormRef.current = document.getElementById('list-your-property-form');
        }
    };

    // Function to scroll to the List Your Property form
    const goToLypForm = () => {
        if (lypFormRef.current) {
            window.scrollTo({
                behavior: 'smooth',
                top:
                    lypFormRef.current.getBoundingClientRect().top
                    - document.body.getBoundingClientRect().top
                    - 250
            });
            trigger('interaction', createInteractionEvent('list_your_property.selected', 'Property Onboarding', 'Property Onboarding', undefined, '2.3.0'));
        }
    };
    // on scroll functions
    const onScroll = () => {
        setTop(isTopOfPage());
        fetchLypForm();
        setIsLypFormVisible(getLypFormVisbility());
    };
    const scrollHandler = throttle(onScroll, 333);
    useEffect(() => {
        if (typeof document === 'object') {
            lypFormRef.current = document.getElementById('list-your-property-form');
        }
        window.addEventListener('scroll', scrollHandler);
        return () => {
            window.removeEventListener('scroll', scrollHandler);
        };
    }, []);
    useEffect(() => {
        setTimeout(() => {
            if (typeof document === 'object') {
                lypFormRef.current = document.getElementById('list-your-property-form');
            }
        }, 300);
    }, [location]);
    return (
        <DomUtilsCtx.Provider value={{
            isTopOfpage: top,
            isLypFormVisible,
            goToLypForm,
            handleLodgingPreferenceChange
        }}
        >
            {children}
        </DomUtilsCtx.Provider>
    );
};

DomUtilsHandler.propTypes = {
    children: PropTypes.node.isRequired,
    // pageflow
    pageFlow: PropTypes.shape({
        next: PropTypes.func,
        nextPageLink: PropTypes.func
    }).isRequired
};

DomUtilsHandler.displayName = DOM_UTILS_CTX;

export default hot(module)(withPageFlow(DomUtilsHandler));
